import * as THREE from 'three';
import { possum } from './builders/decor';
import { buildBull } from './fairgrounds';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, cyl, mat, mesh, sphere } from './materials';
import { PublicRoom } from './PublicRoom';
import { UFO_SLOT } from './rooms';

/** Inside the saucer (local coordinates; you come in on the beam pad by the +z wall). */
export const UFO_SPOTS = {
  table: { x: 0, z: -1.6 },
  button: { x: -3.2, z: 3.4 },
  gizmo: { x: 3.6, z: 0.4 },
  cow: { x: -3.8, z: -3.2 },
  jars: { x: 4.6, z: -3.4 },
  aliens: { x: 1.4, z: -0.2 },
} as const satisfies Record<string, Vec2>;

export interface Alien {
  group: THREE.Group;
  head: THREE.Group;
}

/** A little gray visitor: big head, bigger eyes, no manners. */
export function buildAlien(): Alien {
  const g = new THREE.Group();
  const skin = mat('#a3ad9e', { rough: 0.6 });
  const eye = mat('#0b0b0f', { rough: 0.05, metal: 0.3 });
  g.add(box(0.08, 0.55, 0.08, skin, -0.08, 0.28, 0));
  g.add(box(0.08, 0.55, 0.08, skin, 0.08, 0.28, 0));
  g.add(box(0.3, 0.42, 0.18, skin, 0, 0.75, 0));
  for (const s of [-1, 1]) {
    const arm = box(0.06, 0.62, 0.06, skin, s * 0.2, 0.68, 0.02);
    arm.rotation.z = s * 0.12;
    g.add(arm);
  }
  g.add(cyl(0.04, 0.05, 0.12, skin, 0, 1.01, 0, 6));
  const head = new THREE.Group();
  head.position.y = 1.08;
  const skull = sphere(0.26, skin, 0, 0.2, 0, 14);
  skull.scale.set(1, 1.2, 0.95);
  head.add(skull);
  for (const s of [-1, 1]) {
    const e = sphere(0.08, eye, s * 0.11, 0.2, 0.2, 10);
    e.scale.set(1.3, 0.75, 0.5);
    e.rotation.z = s * -0.45;
    head.add(e);
  }
  g.add(head);
  g.traverse((o) => (o.userData.noBatch = true));
  return { group: g, head };
}

export interface Saucer {
  room: PublicRoom;
  aliens: Alien[];
  table: THREE.Object3D;
  button: THREE.Object3D;
  gizmo: THREE.Object3D;
  cow: THREE.Object3D;
}

export function buildUfoInterior(parent: THREE.Object3D, collision: CollisionWorld): Saucer {
  const aliens: Alien[] = [];
  let table: THREE.Object3D = new THREE.Group();
  let button: THREE.Object3D = new THREE.Group();
  let gizmo: THREE.Object3D = new THREE.Group();
  let cow: THREE.Object3D = new THREE.Group();
  const blinks: THREE.MeshStandardMaterial[] = [];
  const room = new PublicRoom(
    parent,
    collision,
    UFO_SLOT,
    'ufo',
    {
      // Pale, faintly glowing panels: it's lit from inside the walls, like a fridge.
      wall: mat('#c8d2dc', { metal: 0.15, rough: 0.45, emissive: '#5a8a8a', emissiveIntensity: 0.35 }),
      floor: mat('#8a96a2', { metal: 0.2, rough: 0.5, emissive: '#2a3a3a', emissiveIntensity: 0.3 }),
      ceiling: mat('#b8c4ce', { metal: 0.15, rough: 0.5, emissive: '#4a7a7a', emissiveIntensity: 0.3 }),
    },
    (kit) => {
      const { length: L, depth: D, height: H } = kit.shape;
      const glow = (color: string, k = 1.6): THREE.MeshStandardMaterial =>
        new THREE.MeshStandardMaterial({ color, emissive: new THREE.Color(color), emissiveIntensity: k });
      // Light strips around the walls and a glowing dome overhead.
      const strip = glow('#5affd6', 1.4);
      for (const [g, w, x, z, rot] of [
        [kit.walls.back, L, 0, -D / 2 + 0.02, 0],
        [kit.walls.west, D, -L / 2 + 0.02, 0, Math.PI / 2],
        [kit.walls.east, D, L / 2 - 0.02, 0, Math.PI / 2],
        [kit.walls.front, L, 0, D / 2 - 0.02, 0],
      ] as const) {
        const s = box(w - 0.4, 0.08, 0.03, strip, x, 2.6, z, { cast: false });
        s.rotation.y = rot;
        g.add(s);
      }
      const dome = mesh(new THREE.CircleGeometry(3, 32), glow('#c8fff0', 0.9), 0, H - 0.02, 0, {
        cast: false,
      });
      dome.rotation.x = Math.PI / 2;
      kit.ceiling.add(dome);
      // The beam pad you arrive on.
      const pad = mesh(new THREE.CircleGeometry(1.1, 28), glow('#5affb0', 1.2), 0, 0.02, D / 2 - 1.2, {
        cast: false,
      });
      pad.rotation.x = -Math.PI / 2;
      kit.add(pad, 0, D / 2 - 1.2);
      // The exam table, with an operating lamp over it.
      const t = new THREE.Group();
      t.add(cyl(0.25, 0.4, 0.8, mat('#6a727c', { metal: 0.7 }), 0, 0.4, 0, 12));
      t.add(box(2.1, 0.12, 0.85, mat('#d8e0e8', { metal: 0.4, rough: 0.2 }), 0, 0.86, 0));
      for (const x of [-0.5, 0.5]) t.add(box(0.06, 0.04, 0.9, mat('#3a3f46'), x, 0.94, 0));
      table = kit.place(t, UFO_SPOTS.table.x, UFO_SPOTS.table.z);
      kit.solid(UFO_SPOTS.table.x, UFO_SPOTS.table.z, 2.1, 0.85);
      const lamp = mesh(
        new THREE.ConeGeometry(0.6, 0.5, 16, 1, true),
        mat('#8a929c', { metal: 0.6, side: THREE.DoubleSide }),
        0,
        H - 0.6,
        0,
      );
      kit.ceiling.add(lamp.translateX(UFO_SPOTS.table.x).translateZ(UFO_SPOTS.table.z));
      kit.ceiling.add(
        mesh(
          new THREE.CircleGeometry(0.5, 16),
          glow('#ffffff', 2),
          UFO_SPOTS.table.x,
          H - 0.85,
          UFO_SPOTS.table.z,
          { cast: false },
        ).rotateX(Math.PI / 2),
      );
      // The visitors.
      for (const [x, z, rot] of [
        [-1.3, -0.4, 0.6],
        [1.4, -0.2, -0.7],
        [0.3, -3.4, 0],
      ] as const) {
        const a = buildAlien();
        kit.place(a.group, x, z, rot);
        kit.circle(x, z, 0.25);
        aliens.push(a);
      }
      // A cow, in a tube, just floating there.
      const c = UFO_SPOTS.cow;
      kit.add(cyl(1.3, 1.3, 0.15, glow('#5affd6', 1.2), 0, 0.08, 0, 24), c.x, c.z);
      kit.place(
        cyl(
          1.25,
          1.25,
          3.2,
          mat('#bff5ff', { transparent: true, opacity: 0.2, rough: 0.05 }),
          0,
          1.75,
          0,
          24,
          { open: true },
        ),
        c.x,
        c.z,
      );
      const bull = buildBull('#f1ece2');
      bull.group.scale.setScalar(0.85);
      bull.group.position.y = 0.4;
      cow = kit.place(bull.group, c.x, c.z, 0.7);
      kit.circle(c.x, c.z, 1.35);
      // Specimen jars along the east side: a possum, a lawn gnome, a beer can the size of a dog.
      const jarGlass = mat('#bff5ff', { transparent: true, opacity: 0.22, rough: 0.05 });
      const fluid = mat('#7affc0', { transparent: true, opacity: 0.18 });
      const j = UFO_SPOTS.jars;
      for (let i = 0; i < 3; i++) {
        const x = j.x;
        const z = j.z + i * 1.2;
        kit.add(cyl(0.45, 0.45, 0.2, mat('#5a626c', { metal: 0.6 }), 0, 0.1, 0, 16), x, z);
        kit.place(cyl(0.42, 0.42, 1.6, jarGlass, 0, 1.0, 0, 16, { open: true }), x, z);
        kit.place(cyl(0.4, 0.4, 1.4, fluid, 0, 0.95, 0, 16), x, z);
        kit.circle(x, z, 0.45);
      }
      const p = possum();
      p.group.scale.setScalar(1.6);
      p.group.position.y = 0.7;
      kit.place(p.group, j.x, j.z, -1);
      const can = cyl(0.2, 0.2, 0.7, mat('#2f6fd6', { metal: 0.6, rough: 0.3 }), 0, 0.85, 0, 12);
      kit.place(can, j.x, j.z + 2.4);
      const gnome = new THREE.Group();
      gnome.add(cyl(0.16, 0.2, 0.4, mat('#2f6fd6'), 0, 0.7, 0, 8));
      gnome.add(sphere(0.14, mat('#f1c6a8'), 0, 1.0, 0, 8));
      gnome.add(cyl(0.0, 0.16, 0.36, mat('#c62828'), 0, 1.25, 0, 8));
      gnome.add(sphere(0.12, mat('#f4f4f0'), 0, 0.88, 0.08, 8));
      kit.place(gnome, j.x, j.z + 1.2);
      // The console, and the glowing doohickey on a plinth.
      kit.add(box(2.2, 1.0, 0.7, mat('#5a626c', { metal: 0.6 }), 0, 0.5, 0), 3.6, -D / 2 + 0.6);
      kit.solid(3.6, -D / 2 + 0.6, 2.2, 0.7);
      for (let i = 0; i < 10; i++) {
        const m = glow(['#ff3b3b', '#39ff5a', '#3ad6ff', '#ffd400'][i % 4] as string, 1.5);
        blinks.push(m);
        kit.place(box(0.12, 0.04, 0.12, m, 0, 1.02, 0), 3.6 - 0.9 + i * 0.2, -D / 2 + 0.7);
      }
      const g = UFO_SPOTS.gizmo;
      kit.add(cyl(0.25, 0.35, 1.0, mat('#5a626c', { metal: 0.6 }), 0, 0.5, 0, 10), g.x, g.z);
      kit.circle(g.x, g.z, 0.35);
      gizmo = kit.place(
        mesh(new THREE.OctahedronGeometry(0.18, 0), glow('#ff4af0', 2.2), 0, 1.3, 0),
        g.x,
        g.z,
      );
      // The big red button. Everybody knows what a big red button does.
      const b = UFO_SPOTS.button;
      const btn = new THREE.Group();
      btn.add(cyl(0.18, 0.25, 1.0, mat('#3a3f46', { metal: 0.6 }), 0, 0.5, 0, 10));
      btn.add(cyl(0.15, 0.15, 0.1, glow('#ff2a2a', 1.4), 0, 1.06, 0, 16));
      button = kit.place(btn, b.x, b.z);
      kit.circle(b.x, b.z, 0.3);
      kit.animate((time) => {
        aliens.forEach((a, i) => {
          a.head.rotation.z = Math.sin(time * 0.8 + i * 2) * 0.25;
          a.head.rotation.y = Math.sin(time * 0.5 + i) * 0.4;
          a.group.position.y = Math.abs(Math.sin(time * 1.4 + i)) * 0.03;
        });
        cow.position.y = 0.4 + Math.sin(time * 0.9) * 0.15;
        cow.rotation.y = 0.7 + time * 0.15;
        gizmo.rotation.y = time * 2;
        gizmo.position.y = 1.3 + Math.sin(time * 2) * 0.06;
        blinks.forEach((m, i) => (m.emissiveIntensity = Math.sin(time * (2 + i * 0.7) + i) > 0 ? 2 : 0.3));
      });
    },
  );
  return { room, aliens, table, button, gizmo, cow };
}
